import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Box,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
  ButtonBase,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserBadge } from './UserBadge';
import { Logo } from './Logo';
import { apiService } from '../services/api';
import { Tab as TabType } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import { NotificationCenter } from './ui/NotificationCenter';

// Componentes SVG de banderas
const SpainFlag: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size * 0.75} viewBox="0 0 36 27" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="9" fill="#C60B1E"/>
    <rect y="9" width="36" height="9" fill="#FFC400"/>
    <rect y="18" width="36" height="9" fill="#C60B1E"/>
  </svg>
);

const UKFlag: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size * 0.75} viewBox="0 0 36 27" xmlns="http://www.w3.org/2000/svg">
    <rect width="36" height="27" fill="#012169"/>
    <path d="M0 0 L36 27 M36 0 L0 27" stroke="#FFF" strokeWidth="3"/>
    <path d="M0 0 L36 27 M36 0 L0 27" stroke="#C8102E" strokeWidth="2"/>
    <path d="M18 0 L18 27 M0 13.5 L36 13.5" stroke="#FFF" strokeWidth="4"/>
    <path d="M18 0 L18 27 M0 13.5 L36 13.5" stroke="#C8102E" strokeWidth="2.5"/>
    <path d="M0 0 L36 0 L36 27 L0 27 Z" fill="none" stroke="#FFF" strokeWidth="1.5"/>
    <path d="M0 0 L36 0 L36 27 L0 27 Z" fill="none" stroke="#C8102E" strokeWidth="1"/>
  </svg>
);

// Función para mapear nombres de iconos a componentes de Material-UI
const getIconComponent = (iconName: string | null) => {
  if (!iconName) return undefined;
  
  const iconMap: { [key: string]: React.ReactElement } = {
    'dashboard': <DashboardIcon />,
    'admin_panel_settings': <AdminPanelSettingsIcon />,
    'people': <PeopleIcon />,
    'settings': <SettingsIcon />,
    'home': <HomeIcon />,
  };
  
  const normalizedName = iconName.toLowerCase().replace(/-/g, '_');
  return iconMap[normalizedName] || iconMap[iconName.toLowerCase()] || undefined;
};

// Función para limpiar el label y extraer solo el texto legible
const cleanLabel = (label: string, name: string): string => {
  // Si el label contiene el name técnico, extraer solo la parte legible
  if (label.includes(name)) {
    // Buscar el texto después del nombre técnico
    const parts = label.split(name);
    const cleanPart = parts.find(part => part.trim() && part.trim() !== '-');
    if (cleanPart) {
      return cleanPart.trim();
    }
    // Si no hay parte limpia, devolver el label sin el name
    return label.replace(name, '').trim();
  }
  return label;
};

export const TopBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { changeLanguage, currentLanguage } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [tabs, setTabs] = useState<TabType[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState<number | false>(false);
  const [languageMenuEl, setLanguageMenuEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const languageMenuOpen = Boolean(languageMenuEl);

  // Función para encontrar el índice de la tab activa basado en la ruta actual
  const findActiveTabIndex = (tabsList: TabType[], currentPath: string): number | false => {
    // Buscar la tab que mejor coincida con la ruta actual
    // Priorizar coincidencias exactas, luego coincidencias que empiecen con el path
    const exactMatch = tabsList.findIndex(tab => tab.path === currentPath);
    if (exactMatch !== -1) {
      return exactMatch;
    }
    
    // Buscar coincidencias parciales (la ruta empieza con el path de la tab)
    const partialMatch = tabsList.findIndex(tab => currentPath.startsWith(tab.path));
    if (partialMatch !== -1) {
      return partialMatch;
    }
    
    return false;
  };

  useEffect(() => {
    // Solo cargar tabs si el usuario está autenticado
    if (!isAuthenticated || !user) {
      setTabs([]);
      setCurrentTabIndex(false);
      return;
    }

    const loadTabs = async () => {
      // En móviles, las cookies pueden tardar en estar disponibles
      // Intentar cargar tabs con retry
      const maxAttempts = 3;
      const delay = 200; // 200ms entre intentos
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          // Pequeño delay después del primer intento para dar tiempo a las cookies
          if (attempt > 0) {
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
          }
          
          const userTabs = await apiService.getTabs();
        
        // Ordenar pestañas: administración al final
        const sortedTabs = [...userTabs].sort((a, b) => {
          const aIsAdmin = a.path.includes('admin') || a.name.toLowerCase().includes('admin');
          const bIsAdmin = b.path.includes('admin') || b.name.toLowerCase().includes('admin');
          
          if (aIsAdmin && !bIsAdmin) return 1;
          if (!aIsAdmin && bIsAdmin) return -1;
          
          // Si ambas son admin o ninguna, ordenar por order
          return a.order - b.order;
        });
        
          setTabs(sortedTabs);
          
          // Determinar tab actual según la ruta usando índices
          const activeIndex = findActiveTabIndex(sortedTabs, location.pathname);
          setCurrentTabIndex(activeIndex);
          
          // Si llegamos aquí, la petición fue exitosa, salir del loop
          return;
        } catch (error) {
          // Si es el último intento, loguear el error
          if (attempt === maxAttempts - 1) {
            logger.error('Error cargando pestañas después de múltiples intentos:', error);
          }
          // Continuar con el siguiente intento si no es el último
        }
      }
    };

    loadTabs();
  }, [location.pathname, isAuthenticated, user]);

  // Actualizar el índice de la tab activa cuando cambia la ruta
  useEffect(() => {
    if (tabs.length > 0) {
      const activeIndex = findActiveTabIndex(tabs, location.pathname);
      setCurrentTabIndex(activeIndex);
    }
  }, [location.pathname, tabs]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTabIndex(newValue);
    // Navegar a la ruta correspondiente a la tab seleccionada
    if (tabs[newValue]) {
      navigate(tabs[newValue].path);
    }
  };

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageMenuEl(null);
  };

  const handleLanguageChange = async (locale: string) => {
    await changeLanguage(locale);
    handleLanguageClose();
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleMobileTabClick = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: (theme) => theme.custom.gradients.primary,
          borderBottom: (theme) => `1px solid ${alpha(theme.custom.colors.accent.main, 0.2)}`,
          color: 'white',
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        <Toolbar 
          sx={{
            minHeight: { xs: 51.2, sm: 57.6 },
            px: { xs: 2, sm: 3 },
            gap: { xs: 1, sm: 2 },
          }}
        >
          {/* Logo */}
          <ButtonBase
          onClick={() => navigate('/ensayos')}
          aria-label={t('topBar.goHome', 'Ir al panel principal')}
          sx={{
            flexGrow: 0,
            mr: { xs: 1, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            px: 0.5,
            py: 0.5,
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.9,
            },
          }}
        >
          <Logo
            variant="light"
            width={isMobile ? 96 : 112}
            ariaLabel={t('topBar.brandLabel', 'Cervellia Technologies')}
          />
        </ButtonBase>
          
          {/* Pestañas de navegación - Solo en desktop */}
          {!isMobile && tabs.length > 0 && (
            <Box 
              sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                justifyContent: 'center',
                maxWidth: 'calc(100% - 320px)',
              }}
            >
              <Tabs
                aria-label={t('topBar.primaryNavigation', 'Navegación principal')}
                value={currentTabIndex !== false ? currentTabIndex : false}
                onChange={handleTabChange}
                textColor="inherit"
                indicatorColor="secondary"
                sx={{ 
                  minHeight: 57.6,
                  '& .MuiTabs-indicator': {
                    backgroundColor: (theme) => theme.custom.colors.accent.main,
                    height: 3.2,
                    borderRadius: '2px 2px 0 0',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                  '& .MuiTab-root': {
                    minHeight: 57.6,
                    minWidth: { xs: 64, sm: 96 },
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    color: (theme) => theme.palette.common.white,
                    opacity: 1,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.custom.colors.accent.main, 0.15),
                      color: (theme) => theme.palette.common.white,
                      opacity: 1,
                    },
                    '&.Mui-selected': {
                      color: (theme) => theme.palette.common.white,
                      fontWeight: 600,
                      opacity: 1,
                    },
                  },
                }}
                scrollButtons="auto"
                variant="scrollable"
                allowScrollButtonsMobile
              >
                {tabs.map((tab, index) => {
                  const cleanTabLabel = cleanLabel(tab.label, tab.name);
                  const iconComponent = getIconComponent(tab.icon);
                  
                  return (
                    <Tooltip 
                      key={tab.id}
                      title={tab.description || cleanTabLabel}
                      arrow
                      placement="bottom"
                    >
                      <Tab
                        label={cleanTabLabel}
                        value={index}
                        icon={iconComponent}
                        iconPosition="start"
                        sx={{
                          '& .MuiTab-iconWrapper': {
                            marginRight: iconComponent ? 1.5 : 0,
                            fontSize: '1rem',
                          },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Tabs>
            </Box>
          )}

          {/* Botón de menú móvil */}
          {isMobile && tabs.length > 0 && (
            <IconButton
              onClick={handleMobileMenuOpen}
              aria-label={t('topBar.openMobileMenu', 'Abrir menú de navegación')}
              sx={{
                color: 'white',
                ml: 'auto',
                mr: 1,
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.custom.colors.accent.main, 0.2),
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Controles del usuario (idioma y badge) */}
          <Box 
            sx={{ 
              flexGrow: 0, 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1 },
              ml: isMobile ? 0 : 'auto',
            }}
          >
            {/* Notification Center */}
            <NotificationCenter />

            {/* Selector de Idioma */}
            <Tooltip title={t('topBar.changeLanguage')}>
              <IconButton
                onClick={handleLanguageClick}
                aria-label={t('topBar.changeLanguage')}
                aria-haspopup="true"
                aria-expanded={languageMenuOpen}
                sx={{
                  p: { xs: 0.75, sm: 1 },
                  color: 'white',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      `rgba(${parseInt(theme.custom.colors.accent.main.slice(1, 3), 16)}, ${parseInt(
                        theme.custom.colors.accent.main.slice(3, 5),
                        16
                      )}, ${parseInt(theme.custom.colors.accent.main.slice(5, 7), 16)}, 0.2)`,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                {currentLanguage === 'es' ? <SpainFlag size={20} /> : <UKFlag size={20} />}
              </IconButton>
            </Tooltip>

            {/* Menú de Idioma */}
            <Menu
              anchorEl={languageMenuEl}
              open={languageMenuOpen}
              onClose={handleLanguageClose}
              aria-label={t('topBar.languageMenu', 'Selector de idioma')}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: (theme) => theme.shadows[4],
                },
              }}
            >
              <MenuItem 
              onClick={() => handleLanguageChange('es')}
              selected={currentLanguage === 'es'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: (theme) => alpha(theme.custom.colors.accent.main, 0.1),
                },
              }}
            >
              <Box component="span" sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                <SpainFlag size={24} />
              </Box>
              <Typography>{t('userBadge.spanish')}</Typography>
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={currentLanguage === 'en'}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: (theme) => alpha(theme.custom.colors.accent.main, 0.1),
                },
              }}
            >
              <Box component="span" sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                <UKFlag size={24} />
              </Box>
              <Typography>{t('userBadge.english')}</Typography>
            </MenuItem>
          </Menu>

            <UserBadge />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer para menú móvil */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: { xs: 224, sm: 256 },
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            background: (theme) => theme.custom.gradients.primary,
            color: 'white',
            minHeight: 96,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        >
          <Typography variant="h6" color="inherit" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('topBar.navigation', 'Navegación')}
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ opacity: 0.9 }}>
            {user?.email}
          </Typography>
        </Box>
        <List sx={{ pt: 2 }}>
          {tabs.map((tab, index) => {
            const cleanTabLabel = cleanLabel(tab.label, tab.name);
            const iconComponent = getIconComponent(tab.icon);
            const isSelected = location.pathname.startsWith(tab.path);
            
            return (
              <React.Fragment key={tab.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleMobileTabClick(tab.path)}
                    selected={isSelected}
                    sx={{
                      py: 1.5,
                      px: 3,
                      '&.Mui-selected': {
                        backgroundColor: (theme) => alpha(theme.custom.colors.accent.main, 0.1),
                        borderLeft: (theme) => `4px solid ${theme.custom.colors.accent.main}`,
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.custom.colors.accent.main, 0.15),
                        },
                        '& .MuiListItemText-primary': {
                          color: (theme) => theme.custom.colors.accent.main,
                          fontWeight: 600,
                        },
                        '& .MuiListItemIcon-root': {
                          color: (theme) => theme.custom.colors.accent.main,
                        },
                      },
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    {iconComponent && (
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          color: (theme) => isSelected 
                            ? theme.custom.colors.accent.main 
                            : theme.palette.text.secondary,
                        }}
                      >
                        {iconComponent}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={cleanTabLabel}
                      primaryTypographyProps={{
                        fontSize: '0.75rem',
                        fontWeight: isSelected ? 600 : 500,
                        color: (theme) => isSelected 
                          ? theme.custom.colors.accent.main 
                          : theme.palette.text.primary,
                      }}
                      secondary={tab.description ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                          {tab.description}
                        </Typography>
                      ) : null}
                    />
                  </ListItemButton>
                </ListItem>
                {index < tabs.length - 1 && (
                  <Divider sx={{ mx: 2, borderColor: 'divider' }} />
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

